/// <reference types="@types/google.analytics"/>

import { CommonModule } from '@angular/common';
import {
  ModuleWithProviders,
  NgModule,
  Optional,
  SkipSelf,
} from '@angular/core';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { USER_PROVIDED_META_REDUCERS } from '@ngrx/store';
import GoogleAnalytics, { trackPageView } from '@redux-beacon/google-analytics';
import { createMetaReducer, EventsMap } from 'redux-beacon';

export class GoogleAnalyticsConfig {
  trackingId = '';
}

export function metaReducerFactory(config: GoogleAnalyticsConfig) {
  if (typeof window.ga !== 'function') {
    const head = document.getElementsByTagName('head')[0];

    // https://developers.google.com/analytics/devguides/collection/analyticsjs#alternative_async_tag
    const googleAnalyticsFirstScript = document.createElement('script');
    googleAnalyticsFirstScript.innerHTML = `window.ga=window.ga||function(){(ga.q=ga.q||[]).push(arguments)};ga.l=+new Date;
      ga('create', '${config.trackingId}', 'auto');`;

    const googleAnalyticsSecondScript = document.createElement('script');
    googleAnalyticsSecondScript.async = true;
    googleAnalyticsSecondScript.src =
      'https://www.google-analytics.com/analytics.js';

    head.insertBefore(googleAnalyticsSecondScript, head.firstChild);
    head.insertBefore(googleAnalyticsFirstScript, head.firstChild);

    if (location.protocol !== 'https:') {
      // https://developers.google.com/analytics/devguides/collection/analyticsjs/debugging#testing_your_implementation_without_sending_hits
      ga('set', 'sendHitTask', null);
    }
  }

  const eventsMap: EventsMap = {
    [ROUTER_NAVIGATED]: trackPageView((action) => ({
      location: location.origin,
      page: action.payload.routerState.url,
    })),
  };

  return [createMetaReducer(eventsMap, GoogleAnalytics())];
}

@NgModule({
  imports: [CommonModule],
})
export class GoogleAnalyticsModule {
  constructor(
    @Optional() @SkipSelf() parentModule: GoogleAnalyticsModule,
    config: GoogleAnalyticsConfig
  ) {
    // Check if NgModule was previously loaded
    // https://angular.io/guide/ngmodule-faq#how-can-i-tell-if-an-ngmodule-or-service-was-previously-loaded
    if (parentModule) {
      throw new Error(
        'GoogleAnalyticsModule is already loaded. Import it in the AppModule only'
      );
    }

    if (!config) {
      throw new Error(
        'Provide config for GoogleAnalyticsModule using the forRoot method'
      );
    }
  }

  static forRoot(
    config: GoogleAnalyticsConfig
  ): ModuleWithProviders<GoogleAnalyticsModule> {
    return {
      ngModule: GoogleAnalyticsModule,
      providers: [
        {
          provide: GoogleAnalyticsConfig,
          useValue: config,
        },
        {
          provide: USER_PROVIDED_META_REDUCERS,
          deps: [GoogleAnalyticsConfig],
          useFactory: metaReducerFactory,
        },
      ],
    };
  }
}
